import moment from "moment";

export const isPresent = (value) => value !== undefined && value !== null && value !== "";

const pad = (value) => String(value).padStart(2, "0");

const formatMinutes = (minutes) => {
  const wholeMinutes = Math.trunc(minutes);
  return `${pad(Math.floor(wholeMinutes / 60))}:${pad(wholeMinutes % 60)}`;
};

/**
 * Availability lookups expect the strict salon wall-clock shape
 * (``YYYY-MM-DD HH:mm:ss``).  Appointment detail reads serialize a child
 * appointment start as an offset-bearing ISO string for non-UTC salons, so
 * edit hydration must normalize a child's start time before reusing it as an
 * add-on / add-another availability context.
 */
export const normalizeAppointmentAvailabilityDateTime = (value) => {
  if (!isPresent(value) || typeof value === "number") return "";
  const text = String(value).trim();
  const wall = moment(text, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
  if (wall.isValid()) return wall.format("YYYY-MM-DD HH:mm:ss");
  const iso = moment.parseZone(text, moment.ISO_8601, true);
  return iso.isValid() ? iso.format("YYYY-MM-DD HH:mm:ss") : "";
};

export const normalizeAppointmentServiceTime = (value) => {
  if (!isPresent(value)) return "";

  if (typeof value === "number" || /^\d+(?:\.\d+)?$/.test(String(value).trim())) {
    const minutes = Number(value);
    return Number.isFinite(minutes) && minutes >= 0 ? formatMinutes(minutes) : "";
  }

  const parts = String(value).trim().split(":");
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) {
    return "";
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  const seconds = parts.length === 3 ? Number(parts[2]) : 0;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return "";
  }
  if (minutes > 59 || seconds > 59 || hours < 0) return "";

  return `${pad(hours)}:${pad(minutes)}`;
};

const addAppointmentServiceTimes = (duration, padding) => {
  if (!duration) return padding;
  if (!padding) return duration;

  const [durationHours, durationMinutes] = duration.split(":").map(Number);
  const [paddingHours, paddingMinutes] = padding.split(":").map(Number);
  return formatMinutes(
    durationHours * 60 + durationMinutes + paddingHours * 60 + paddingMinutes,
  );
};

const readStaffId = (value) => {
  if (!isPresent(value)) return "";
  if (typeof value !== "object") return value;
  return value.id ?? value.value ?? value.staff_id ?? "";
};

export const resolveAppointmentServiceStaffId = (service = {}, parentStaffId = "") => {
  const staff = service && typeof service.staff === "object" ? service.staff : {};
  return (
    readStaffId(service.staff_id) ||
    readStaffId(service.staffId) ||
    readStaffId(staff) ||
    readStaffId(service.assigned_staff_id) ||
    readStaffId(parentStaffId)
  );
};

export const buildAppointmentServiceFields = (service = {}, parentStaffId = "") => {
  const duration = normalizeAppointmentServiceTime(service.duration);
  const padding_time = normalizeAppointmentServiceTime(service.padding_time);
  const durationWithPadding =
    normalizeAppointmentServiceTime(service.durationWithPadding) ||
    addAppointmentServiceTimes(duration, padding_time);

  return {
    ...(isPresent(service.service_id) ? { service_id: service.service_id } : {}),
    staff_id: resolveAppointmentServiceStaffId(service, parentStaffId),
    duration,
    padding_time,
    durationWithPadding,
  };
};

export const buildEmptyAdditionalServiceLine = (parentStaffId = "") => ({
  service_id: "",
  staff_id: isPresent(parentStaffId) ? parentStaffId : "",
  duration: "",
  cost: "",
  resource_item_id: "",
});

const getStaffLabel = (service = {}, staffId, parentStaffOptions = []) => {
  const staff = service && typeof service.staff === "object" ? service.staff : {};
  const parentOption = Array.isArray(parentStaffOptions)
    ? parentStaffOptions.find((option) => String(readStaffId(option)) === String(staffId))
    : null;
  return (
    service.staff_name ||
    staff.label ||
    staff.name ||
    [staff.first_name, staff.last_name].filter(Boolean).join(" ") ||
    parentOption?.label ||
    parentOption?.name ||
    String(staffId)
  );
};

export const appendAppointmentStaffOption = (
  options,
  service = {},
  parentStaffId = "",
  parentStaffOptions = [],
) => {
  const safeOptions = Array.isArray(options) ? options : [];
  const staffId = resolveAppointmentServiceStaffId(service, parentStaffId);
  if (!isPresent(staffId)) return safeOptions;

  const hasStaff = safeOptions.some(
    (option) => option && String(option.value) === String(staffId),
  );
  return hasStaff
    ? safeOptions
    : [...safeOptions, { value: staffId, label: getStaffLabel(service, staffId, parentStaffOptions) }];
};

export const buildAppointmentChildOptionState = ({
  existing = {},
  id,
  service = {},
  parentStaffId = "",
  parentStaffOptions = [],
  staffOptions,
  resourceOptions,
}) => {
  const existingStaff = Array.isArray(existing.staff) ? existing.staff : [];
  const existingResources = Array.isArray(existing.resource_items) ? existing.resource_items : [];
  const nextStaff = Array.isArray(staffOptions) ? staffOptions : existingStaff;
  const nextResources = Array.isArray(resourceOptions) || Array.isArray(resourceOptions?.resource_requirements)
    ? resourceOptions : existingResources;

  return {
    ...existing,
    id: isPresent(id) ? id : existing.id,
    staff: appendAppointmentStaffOption(nextStaff, service, parentStaffId, parentStaffOptions),
    resource_items: nextResources,
    ...(resourceOptions !== undefined ? { resource_options_loaded: resourceOptions !== null } : {}),
  };
};
